import { useState } from 'react'

import { Form } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useRouter } from '@tribeplatform/react-sdk'
import {
  useNetwork,
  useUpdateNetworkNotificationSettings,
} from '@tribeplatform/react-sdk/hooks'
import { ActionPanel } from '@tribeplatform/react-ui-kit/ActionPanel'
import { Alert } from '@tribeplatform/react-ui-kit/Alert'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { Link } from '@tribeplatform/react-ui-kit/Link'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { UpgradeBanner } from '../../UpgradeBanner.js'
import { FromEmailPreview } from './FromEmailPreview.js'

export const FromEmailSettings = () => {
  const { $t } = useI18n()
  const { data: network } = useNetwork({
    fields: {
      limits: 'all',
      settings: 'all',
    },
  })
  const { mutateAsync: updateNotificationSettings, isLoading } =
    useUpdateNetworkNotificationSettings()
  const { Link: RouterLink } = useRouter()

  const [expanded, setExpanded] = useState(false)

  const emailFromAuthorized =
    network?.limits?.usageLimits?.emailCustomization ?? false

  const onSubmit = async ({ emailFrom }) => {
    try {
      if (!emailFrom) {
        emailFrom = null
      }

      await updateNotificationSettings({
        input: { emailFrom },
      })
      toast({
        title: $t({
          defaultMessage: 'From email address updated.',
          id: 'Admin.NotificationSettings.FromEmail.Updated',
        }),
        status: 'success',
      })
    } catch (err) {
      toast({
        title: $t({
          defaultMessage: 'Unable to update from email address.',
          id: 'Admin.NotificationSettings.FromEmail.FailedToUpdate',
        }),
        status: 'error',
      })
      logger.error('Unabled to update community notification settings', err)
    }
  }

  if (!expanded) {
    return (
      <ActionPanel
        title={
          <div className="text-md">
            <span>
              <T
                defaultMessage="From email:"
                id="Admin.NotificationSettings.FromEmail"
              />
            </span>{' '}
            <span className="font-normal text-content-subdued">
              {network?.settings?.emailFrom || 'notifications@bettermode.com'}
            </span>
          </div>
        }
        placement="trailing-top"
        description={$t({
          defaultMessage:
            'By default, all emails will be sent from this address to your members',
          id: 'Admin.NotificationSettings.FromEmail.DefaultAddressNotice',
        })}
      >
        <Button
          variant="secondaryNeutral"
          size="lg"
          onClick={() => setExpanded(true)}
        >
          <T
            defaultMessage="Change email"
            id="Admin.NotificationSettings.FromEmail.Change"
          />
        </Button>
      </ActionPanel>
    )
  }

  return (
    <Card>
      <Card.Header
        title={$t({
          defaultMessage: 'From email address',
          id: 'Admin.NotificationSettings.FromEmailAddress',
        })}
        description={$t({
          defaultMessage:
            'By default, all emails will be sent from this address to your members',
          id: 'Admin.NotificationSettings.FromEmail.DefaultAddressNotice',
        })}
      />
      <Card.Content>
        <Form<{ emailFrom: string }>
          defaultValues={network.settings}
          onSubmit={onSubmit}
        >
          {({ watch }) => {
            const [emailFrom] = watch(['emailFrom'])

            const emailFromValue = emailFrom || 'notifications@bettermode.com'
            const emailFromDomain =
              emailFromValue.split('@')?.[1] || 'bettermode.com'
            return (
              <div className="web:col-span-12 web:md:col-span-8 flex flex-col space-y-5">
                {!emailFromAuthorized ? (
                  <>
                    <UpgradeBanner>
                      <T
                        defaultMessage='Changing the "from" email address is available as an <link>add-on</link>.'
                        id="Admin.NotificationSettings.FromEmail.RequiredAddon"
                        values={{
                          link: chunks => (
                            <Link
                              as={RouterLink}
                              href="/manage/app-store/addons?category=emailCustomization"
                            >
                              {chunks}
                            </Link>
                          ),
                        }}
                      />
                    </UpgradeBanner>
                    <div>
                      <span className="block font-medium text-content mb-2">
                        <T
                          defaultMessage="Current email preview"
                          id="Admin.NotificationSettings.FromEmail.Preview"
                        />
                      </span>
                      <FromEmailPreview
                        network={network}
                        emailFrom={emailFromValue}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <Form.Input
                      name="emailFrom"
                      type="email"
                      placeholder="notifications@bettermode.com"
                      disabled={!emailFromAuthorized}
                      label={$t({
                        defaultMessage: 'Email address',
                        id: 'Generics.EmailAddress',
                      })}
                    />
                    {emailFromDomain !== 'bettermode.com' && (
                      <Alert status="info">
                        <T
                          defaultMessage="To ensure your emails will not end up in spam folder, you'll need to add some DNS records on your side. Please contact {support_email} for assistance."
                          id="Admin.NotificationSettings.FromEmail.DnsNotice"
                          values={{
                            support_email: (
                              <Link
                                variant="accent"
                                href="mailto:support@bettermode.com"
                                // eslint-disable-next-line react/jsx-no-literals
                              >
                                support@bettermode.com
                              </Link>
                            ),
                          }}
                        />
                      </Alert>
                    )}
                  </>
                )}

                <div>
                  <span className="block font-medium text-content mb-2">
                    <T
                      defaultMessage="Email preview after update"
                      id="Admin.NotificationSettings.FromEmail.PreviewAfterUpdate"
                    />
                  </span>
                  <FromEmailPreview
                    network={network}
                    emailFrom={
                      !emailFromAuthorized
                        ? 'community@your-domain.com'
                        : emailFromValue
                    }
                    domain={
                      !emailFromAuthorized ? 'your-domain.com' : emailFromDomain
                    }
                    preview={!emailFromAuthorized}
                  />
                </div>

                <Form.Actions>
                  <Button
                    type="submit"
                    loading={isLoading}
                    disabled={!emailFromAuthorized}
                  >
                    <T defaultMessage="Update" id="Generics.Update" />
                  </Button>
                  <Button
                    variant="secondaryNeutral"
                    onClick={() => setExpanded(false)}
                  >
                    <T defaultMessage="Cancel" id="Generics.Cancel" />
                  </Button>
                </Form.Actions>
              </div>
            )
          }}
        </Form>
      </Card.Content>
    </Card>
  )
}
